import {
	AUTOMATIC_ID,
	baseScopesRequiredPerField,
} from '@atlassian/ui-modifications-core/constants';

import type {
	LookupUser,
	LookupUsers,
	PublicToInternalPropertyTransformerContext,
} from '@atlassian/ui-modifications-core/types';
import { FieldValueLookupError } from '@atlassian/ui-modifications-core/utils';
import { getAutomaticUserOption } from '@atlassian/jira-issue-field-assignee/src/common/utils.tsx';
import { isNotEmptyString } from '@atlassian/ui-modifications-public-api-utils/is-not-empty-string';
import { singleValueLookup } from '../utils/single-value-lookup/index.tsx';

export type AssigneeInternalValue = null | {
	accountId: string;
	displayName: string;
	avatarUrls: {
		['48x48']?: string;
		[key: string]: string | undefined;
	};
};

export type AssigneePublicSetValue = null | string;

export type AssigneePublicGetValue = null | {
	accountId: string;
};

export const assigneeSetValueValidator = (value: unknown): value is AssigneePublicSetValue =>
	value === null || isNotEmptyString(value);

export const assigneeSetValueTransformer = (
	value: AssigneePublicSetValue,
	lookupValues: LookupUsers | undefined,
	context: PublicToInternalPropertyTransformerContext,
): AssigneeInternalValue => {
	if (value === null) {
		return null;
	}

	const { intl } = context;

	if (value === AUTOMATIC_ID && intl) {
		// @ts-expect-error deprecated intl shape in getAutomaticUserOption
		const automaticUserOption = getAutomaticUserOption(intl);

		return {
			accountId: automaticUserOption.accountId ?? AUTOMATIC_ID,
			displayName: automaticUserOption.displayName,
			avatarUrls: { '48x48': '' },
		};
	}

	const foundValue = singleValueLookup<LookupUser>(value, lookupValues, 'accountId');

	if (!foundValue) {
		throw new FieldValueLookupError();
	}

	const { accountId, name: displayName, picture } = foundValue;

	return {
		accountId,
		displayName,
		avatarUrls: { '48x48': picture },
	};
};

// Sometimes the assignee value contains extra properties that we want to filter out
export const assigneeGetValueTransformer = (
	value: AssigneeInternalValue,
): AssigneePublicGetValue => {
	if (value === null) {
		return null;
	}
	const { accountId } = value;
	return { accountId };
};

export const assigneeScopes = {
	...baseScopesRequiredPerField,
	granular: [...baseScopesRequiredPerField.granular, 'read:user:jira'],
	coarse: [...baseScopesRequiredPerField.coarse, 'read:jira-user'],
};
